import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Accessibility, Euro, Clock, MapPin, AlertCircle, Loader2, Filter, Navigation } from "lucide-react";
import MapReflow from "./MapReflow";

const T = {
  it: { title: "Servizi Igienici", subtitle: "Bagni pubblici nelle vicinanze", searching: "Cerco servizi...", noResults: "Nessun servizio trovato nei dintorni.", retry: "Riprova", free: "Gratuito", paid: "A pagamento", accessible: "Accessibile", notAccessible: "Non accessibile", hours: "Orari", more: "altri", filter: "Filtra", all: "Tutti", onlyAccessible: "Solo accessibili", onlyFree: "Solo gratuiti", openInMaps: "Apri in mappa", youAreHere: "Sei qui", locationDenied: "Attiva la posizione per i WC più vicini a te", locateMe: "Attiva GPS", locating: "Individuo la tua posizione...", useMyLocation: "La mia posizione", attribution: "Dati © OpenStreetMap contributors" },
  en: { title: "Public Toilets", subtitle: "Restrooms nearby", searching: "Searching...", noResults: "No toilets found nearby.", retry: "Retry", free: "Free", paid: "Paid", accessible: "Accessible", notAccessible: "Not accessible", hours: "Hours", more: "more", filter: "Filter", all: "All", onlyAccessible: "Accessible only", onlyFree: "Free only", openInMaps: "Open in maps", youAreHere: "You are here", locationDenied: "Enable location to see the nearest toilets", locateMe: "Enable GPS", locating: "Finding your location...", useMyLocation: "My location", attribution: "Data © OpenStreetMap contributors" },
  fr: { title: "Toilettes publiques", subtitle: "Toilettes à proximité", searching: "Recherche...", noResults: "Aucune toilette trouvée à proximité.", retry: "Réessayer", free: "Gratuit", paid: "Payant", accessible: "Accessible", notAccessible: "Non accessible", hours: "Horaires", more: "autres", filter: "Filtrer", all: "Tous", onlyAccessible: "Accessibles seulement", onlyFree: "Gratuits seulement", openInMaps: "Ouvrir dans Maps", youAreHere: "Vous êtes ici", locationDenied: "Activez la localisation pour voir les toilettes proches", locateMe: "Activer le GPS", locating: "Localisation en cours...", useMyLocation: "Ma position", attribution: "Données © OpenStreetMap contributors" },
  es: { title: "Aseos públicos", subtitle: "Baños cercanos", searching: "Buscando...", noResults: "No se encontraron baños cercanos.", retry: "Reintentar", free: "Gratis", paid: "De pago", accessible: "Accesible", notAccessible: "No accesible", hours: "Horario", more: "más", filter: "Filtrar", all: "Todos", onlyAccessible: "Solo accesibles", onlyFree: "Solo gratis", openInMaps: "Abrir en mapas", youAreHere: "Estás aquí", locationDenied: "Activa la ubicación para ver los baños cercanos", locateMe: "Activar GPS", locating: "Localizando...", useMyLocation: "Mi ubicación", attribution: "Datos © OpenStreetMap contributors" },
  de: { title: "Öffentliche Toiletten", subtitle: "Toiletten in der Nähe", searching: "Suche...", noResults: "Keine Toiletten in der Nähe gefunden.", retry: "Erneut versuchen", free: "Gratis", paid: "Kostenpflichtig", accessible: "Barrierefrei", notAccessible: "Nicht barrierefrei", hours: "Öffnungszeiten", more: "mehr", filter: "Filter", all: "Alle", onlyAccessible: "Nur barrierefrei", onlyFree: "Nur kostenlos", openInMaps: "In Karten öffnen", youAreHere: "Du bist hier", locationDenied: "Standort aktivieren, um Toiletten in der Nähe zu sehen", locateMe: "GPS aktivieren", locating: "Standort wird ermittelt...", useMyLocation: "Mein Standort", attribution: "Daten © OpenStreetMap contributors" },
};

// Custom marker icon (modern flat pin)
const createMarkerIcon = (accessible = false, fee = null) => {
  const color = accessible ? "#10b981" : "#0ea5e9";
  const symbol = fee === "yes" ? "€" : "WC";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="44" viewBox="0 0 34 44">
    <defs><filter id="ds" x="-40%" y="-20%" width="180%" height="160%"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.28)"/></filter></defs>
    <path filter="url(#ds)" d="M17 1.5c-7.7 0-14 6-14 13.6 0 5.2 3.4 10 6.9 13.6 2.1 2.1 4.2 3.9 5.4 5.6.9 1.2 2.5 1.2 3.4 0 1.2-1.7 3.3-3.5 5.4-5.6C27.6 25.1 31 20.3 31 15.1 31 7.5 24.7 1.5 17 1.5z" fill="${color}"/>
    <circle cx="17" cy="15.2" r="9.4" fill="white"/>
    <text x="17" y="19" text-anchor="middle" font-family="'Space Grotesk','Segoe UI',sans-serif" font-size="9" font-weight="800" letter-spacing="-0.3" fill="${color}">${symbol}</text>
  </svg>`;
  return L.divIcon({
    html: svg,
    className: "toilet-marker",
    iconSize: [34, 44],
    iconAnchor: [17, 44],
    popupAnchor: [0, -40],
  });
};

const userIcon = L.divIcon({
  html: `<div style="width:18px;height:18px;background:#d04260;border:3px solid white;border-radius:50%;box-shadow:0 0 0 2px #d04260,0 2px 6px rgba(0,0,0,0.3);"></div>`,
  className: "user-marker",
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

// Pulsing live GPS marker for the guest
const gpsIcon = L.divIcon({
  html: `<div style="position:relative;width:22px;height:22px;">
    <div style="position:absolute;inset:-12px;background:#3b82f6;opacity:0.18;border-radius:50%;animation:pulseGps 2s infinite;"></div>
    <div style="width:22px;height:22px;background:#3b82f6;border:4px solid white;border-radius:50%;box-shadow:0 0 0 2px #3b82f6,0 4px 12px rgba(59,130,246,0.5);"></div>
  </div>
  <style>@keyframes pulseGps{0%{transform:scale(0.6);opacity:0.5}70%{transform:scale(1.5);opacity:0}100%{opacity:0}}</style>`,
  className: "gps-marker",
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

// Component to fit bounds to all markers — SOLO al primo caricamento dati (o
// quando cambia origine/filtro), MAI ad ogni render: così lo zoom manuale
// dell'utente non viene "rimbalzato" indietro.
const FitBounds = ({ points, fitKey }) => {
  const map = useMap();
  const lastKey = useRef(null);
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (lastKey.current === fitKey) return;
    try {
      const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds.pad(0.15), { maxZoom: 16, animate: false });
      lastKey.current = fitKey;
    } catch { /* bounds transitori */ }
  }, [points, fitKey, map]);
  return null;
};

const formatDistance = (m) => {
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
};

const haversine = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export default function ToiletsMap({ lat, lng, locationLabel, lang = "it" }) {
  const t = T[lang] || T.it;
  const [toilets, setToilets] = useState([]);
  const toiletsRef = useRef([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all"); // all | accessible | free
  const [userPos, setUserPos] = useState(null); // {lat, lng, accuracy}
  const [gpsState, setGpsState] = useState("idle"); // idle | requesting | granted | denied | error

  // Ask GPS permission on mount (auto-trigger)
  useEffect(() => {
    if (!("geolocation" in navigator)) { setGpsState("error"); return; }
    setGpsState("requesting");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGpsState("granted");
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) setGpsState("denied");
        else setGpsState("error");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  }, []);

  const requestLocation = () => {
    if (!("geolocation" in navigator)) return;
    setGpsState("requesting");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGpsState("granted");
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) setGpsState("denied");
        else setGpsState("error");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // origin = user GPS if granted, otherwise property/host location
  const originLat = userPos?.lat ?? lat;
  const originLng = userPos?.lng ?? lng;

  // Overpass è pubblico e a volte va in 429/timeout: proviamo più mirror in
  // sequenza con abort, e in caso di fallimento NON svuotiamo i risultati già
  // ottenuti (i WC restano visibili). L'errore rosso appare solo se non
  // abbiamo proprio nulla da mostrare.
  const doFetch = useCallback(async () => {
    if (!originLat || !originLng) return;
    setLoading(true);
    setError(false);
    const query = `[out:json][timeout:25];(node["amenity"="toilets"](around:2000,${originLat},${originLng});way["amenity"="toilets"](around:2000,${originLat},${originLng});relation["amenity"="toilets"](around:2000,${originLat},${originLng}););out center;`;
    const MIRRORS = [
      "https://overpass-api.de/api/interpreter",
      "https://overpass.kumi.systems/api/interpreter",
      "https://maps.mail.ru/osm/tools/overpass/api/interpreter",
    ];
    let ok = false;
    for (const base of MIRRORS) {
      try {
        const ctrl = new AbortController();
        const to = setTimeout(() => ctrl.abort(), 15000);
        const res = await fetch(`${base}?data=${encodeURIComponent(query)}`, { signal: ctrl.signal });
        clearTimeout(to);
        if (!res.ok) throw new Error("overpass " + res.status);
        const data = await res.json();
        const list = (data.elements || []).map(el => {
          const pLat = el.lat || el.center?.lat;
          const pLng = el.lon || el.center?.lon;
          if (!pLat || !pLng) return null;
          return {
            id: el.id,
            type: el.type,
            lat: pLat,
            lng: pLng,
            tags: el.tags || {},
            distance: haversine(originLat, originLng, pLat, pLng),
          };
        }).filter(Boolean).sort((a, b) => a.distance - b.distance);
        setToilets(list);
        toiletsRef.current = list;
        ok = true;
        break;
      } catch { /* prova il mirror successivo */ }
    }
    if (!ok && toiletsRef.current.length === 0) setError(true);
    setLoading(false);
  }, [originLat, originLng]);

  useEffect(() => { doFetch(); }, [doFetch]);

  const filtered = useMemo(() => {
    if (filter === "accessible") return toilets.filter(t => t.tags.wheelchair === "yes" || t.tags.wheelchair === "designated");
    if (filter === "free") return toilets.filter(t => t.tags.fee === "no" || !t.tags.fee);
    return toilets;
  }, [toilets, filter]);

  if (!lat || !lng) {
    return (
      <div className="text-center py-12 px-5">
        <AlertCircle className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
        <p className="text-sm text-zinc-500">Posizione non configurata.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header info card */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-xs text-zinc-500 flex items-center gap-1 min-w-0 flex-1">
          {gpsState === "granted" ? (
            <>
              <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse shrink-0" />
              <span className="truncate font-bold text-blue-700">{t.youAreHere}</span>
            </>
          ) : (
            <>
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate">{locationLabel || `${lat?.toFixed?.(4)}, ${lng?.toFixed?.(4)}`}</span>
            </>
          )}
        </p>
        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider shrink-0">{toilets.length} WC</span>
      </div>

      {/* GPS state banners */}
      {gpsState === "denied" && (
        <div className="mb-3">
          <button
            onClick={requestLocation}
            className="w-full flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 text-left hover:bg-amber-100 transition-colors"
            data-testid="toilets-gps-retry"
          >
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="text-[11px] text-amber-700 font-medium flex-1">{t.locationDenied}</span>
            <span className="text-[10px] font-bold text-amber-700 uppercase">{t.locateMe}</span>
          </button>
        </div>
      )}
      {gpsState === "requesting" && (
        <div className="mb-3 flex items-center gap-2 text-[11px] text-zinc-500">
          <Loader2 className="w-3 h-3 animate-spin" /> {t.locating}
        </div>
      )}

      {/* Filter chips + locate-me button */}
      <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
        {[
          { id: "all", label: t.all, icon: Filter },
          { id: "free", label: t.onlyFree, icon: Euro },
          { id: "accessible", label: t.onlyAccessible, icon: Accessibility },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`toilet-filter-${f.id}`}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
              filter === f.id ? "bg-zinc-900 text-white" : "bg-white text-zinc-500 border border-zinc-200"
            }`}
          >
            <f.icon className="w-3 h-3" />
            {f.label}
          </button>
        ))}
        {gpsState === "granted" && (
          <button
            onClick={requestLocation}
            data-testid="toilets-locate-me"
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all bg-blue-500 text-white hover:bg-blue-600"
            title={t.locateMe}
          >
            <Navigation className="w-3 h-3" />
            {t.useMyLocation}
          </button>
        )}
      </div>

      {/* MAP */}
      <div className="relative h-[420px] w-full rounded-3xl overflow-hidden shadow-sm border border-zinc-200" data-testid="toilets-map">
        {loading && (
          <div className="absolute inset-0 z-[500] bg-white/90 backdrop-blur flex flex-col items-center justify-center">
            <Loader2 className="w-7 h-7 text-zinc-400 animate-spin mb-3" />
            <p className="text-xs font-bold text-zinc-500">{t.searching}</p>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 z-[500] bg-white flex flex-col items-center justify-center px-6 text-center">
            <AlertCircle className="w-10 h-10 text-red-400 mb-2" />
            <p className="text-sm text-zinc-700 mb-3">Errore nel caricamento.</p>
            <button onClick={doFetch} className="text-xs font-bold bg-zinc-900 text-white px-4 py-2 rounded-full">{t.retry}</button>
          </div>
        )}
        <MapContainer
          center={[originLat, originLng]}
          zoom={15}
          scrollWheelZoom={false}
          style={{ width: "100%", height: "100%" }}
        >
          <MapReflow />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {/* Property/host marker (always shown) */}
          <Marker position={[lat, lng]} icon={userIcon}>
            <Popup>
              <div className="text-xs font-bold" data-testid="toilet-property-popup">🏠 {locationLabel || "Struttura"}</div>
            </Popup>
          </Marker>
          {/* Live GPS marker (only when granted) */}
          {userPos && (
            <Marker position={[userPos.lat, userPos.lng]} icon={gpsIcon}>
              <Popup>
                <div className="text-xs font-bold text-blue-700" data-testid="toilet-gps-popup">📍 {t.youAreHere}</div>
              </Popup>
            </Marker>
          )}
          {filtered.map(toi => {
            const accessible = toi.tags.wheelchair === "yes" || toi.tags.wheelchair === "designated";
            return (
              <Marker
                key={`${toi.type}-${toi.id}`}
                position={[toi.lat, toi.lng]}
                icon={createMarkerIcon(accessible, toi.tags.fee)}
              >
                <Popup>
                  <div className="min-w-[180px]" data-testid="toilet-popup">
                    <p className="font-bold text-sm mb-1">{toi.tags.name || t.title}</p>
                    <p className="text-[10px] text-zinc-500 mb-2">{formatDistance(toi.distance)}</p>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1 text-[11px]">
                        <Euro className="w-3 h-3 text-zinc-400" />
                        <span>{toi.tags.fee === "yes" ? t.paid : t.free}</span>
                        {toi.tags.charge && <span className="text-zinc-400">({toi.tags.charge})</span>}
                      </div>
                      <div className="flex items-center gap-1 text-[11px]">
                        <Accessibility className="w-3 h-3 text-zinc-400" />
                        <span>{accessible ? t.accessible : t.notAccessible}</span>
                      </div>
                      {toi.tags.opening_hours && (
                        <div className="flex items-center gap-1 text-[11px]">
                          <Clock className="w-3 h-3 text-zinc-400" />
                          <span className="truncate">{toi.tags.opening_hours}</span>
                        </div>
                      )}
                    </div>
                    <a
                      href={userPos
                        ? `https://www.google.com/maps/dir/?api=1&origin=${userPos.lat},${userPos.lng}&destination=${toi.lat},${toi.lng}&travelmode=walking`
                        : `https://www.google.com/maps/dir/?api=1&destination=${toi.lat},${toi.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-2 text-[11px] font-bold text-sky-600 hover:underline"
                    >
                      {t.openInMaps} →
                    </a>
                  </div>
                </Popup>
              </Marker>
            );
          })}
          <FitBounds
            points={[
              { lat: originLat, lng: originLng },
              ...(userPos ? [{ lat: userPos.lat, lng: userPos.lng }] : []),
              ...filtered,
            ]}
            fitKey={`${originLat},${originLng},${filter},${filtered.length}`}
          />
        </MapContainer>
      </div>

      {/* LIST */}
      {!loading && !error && (
        <div className="mt-5 space-y-2">
          {filtered.length === 0 ? (
            <p className="text-center text-sm text-zinc-400 py-6">{t.noResults}</p>
          ) : (
            filtered.slice(0, 10).map(toi => {
              const accessible = toi.tags.wheelchair === "yes" || toi.tags.wheelchair === "designated";
              const paid = toi.tags.fee === "yes";
              return (
                <motion.a
                  key={`l-${toi.type}-${toi.id}`}
                  href={userPos
                    ? `https://www.google.com/maps/dir/?api=1&origin=${userPos.lat},${userPos.lng}&destination=${toi.lat},${toi.lng}&travelmode=walking`
                    : `https://www.google.com/maps/dir/?api=1&destination=${toi.lat},${toi.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileTap={{ scale: 0.99 }}
                  className="flex items-center gap-3 p-3 bg-white rounded-2xl shadow-sm border border-zinc-100 hover:border-zinc-300 transition-colors"
                  data-testid="toilet-item"
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${accessible ? "bg-emerald-50" : "bg-sky-50"}`}>
                    <span className={`font-black text-xs ${accessible ? "text-emerald-600" : "text-sky-600"}`}>{paid ? "€" : "WC"}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-zinc-900 truncate">{toi.tags.name || t.title}</p>
                    <div className="flex items-center gap-2 mt-0.5 text-[10px] text-zinc-500">
                      <span>{formatDistance(toi.distance)}</span>
                      <span>•</span>
                      <span>{paid ? t.paid : t.free}</span>
                      {accessible && (<><span>•</span><span className="text-emerald-600 font-bold">{t.accessible}</span></>)}
                    </div>
                  </div>
                  <span className="text-zinc-300 text-xl shrink-0">›</span>
                </motion.a>
              );
            })
          )}
          {filtered.length > 10 && (
            <p className="text-center text-[11px] text-zinc-400 py-2">+ {filtered.length - 10} {t.more}</p>
          )}
        </div>
      )}

      <p className="text-center text-[9px] text-zinc-400 mt-4 font-medium">{t.attribution}</p>
    </div>
  );
}
